"use client";

import { Avatar } from "./Avatar";

interface AvatarGroupUser {
  id: string;
  name?: string;
  src?: string;
  color?: string;
  isEditing?: boolean;
}

interface AvatarGroupProps {
  users: AvatarGroupUser[];
  max?: number;
  size?: "xs" | "sm" | "md" | "lg";
  showNames?: boolean;
  className?: string;
}

export function AvatarGroup({
  users,
  max = 5,
  size = "md",
  showNames = false,
  className = "",
}: AvatarGroupProps) {
  const visibleUsers = users.slice(0, max);
  const remainingCount = users.length - max;

  const overlapStyles: Record<typeof size, string> = {
    xs: "-ml-1.5",
    sm: "-ml-2",
    md: "-ml-2.5",
    lg: "-ml-3",
  };

  return (
    <div className={`flex items-center ${className}`}>
      <div className="flex items-center">
        {visibleUsers.map((user, index) => (
          <div
            key={user.id}
            className={index > 0 ? overlapStyles[size] : ""}
            style={{ zIndex: visibleUsers.length - index }}
          >
            <Avatar
              src={user.src}
              name={user.name}
              color={user.color}
              size={size}
              showRing
              isEditing={user.isEditing}
            />
          </div>
        ))}

        {/* Overflow count */}
        {remainingCount > 0 && (
          <div
            className={`
              ${overlapStyles[size]}
              flex items-center justify-center
              rounded-full bg-neutral-200 text-neutral-600
              text-xs font-medium ring-2 ring-white
              ${size === "xs" ? "w-5 h-5" : size === "sm" ? "w-6 h-6" : size === "lg" ? "w-9 h-9" : "w-7 h-7"}
            `}
            style={{ zIndex: 0 }}
          >
            +{remainingCount}
          </div>
        )}
      </div>

      {/* Names (optional) */}
      {showNames && visibleUsers.length > 0 && (
        <div className="ml-3 hidden sm:block">
          <span className="text-sm text-neutral-600">
            {visibleUsers.map((u) => u.name).join(", ")}
            {remainingCount > 0 && ` 외 ${remainingCount}명`}
          </span>
        </div>
      )}
    </div>
  );
}

export { type AvatarGroupProps, type AvatarGroupUser };
